import React, { useEffect, useRef, useState } from 'react';
import './ScrollableTabs.css';

type ScrollableTabsProps = {
  tabs: string[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
  variant?: 'underline' | 'pill';
};

export function ScrollableTabs({
  tabs,
  activeTab,
  onTabChange,
  className,
  variant = 'underline',
}: ScrollableTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const updateScrollState = () => {
      const left = node.scrollLeft > 2;
      const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 2;
      setCanScrollLeft(left);
      setCanScrollRight(right);
    };

    updateScrollState();

    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(node);
    node.querySelectorAll('.tabs-item').forEach((item) => observer.observe(item));
    node.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      observer.disconnect();
      node.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [tabs]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const node = scrollRef.current;
    if (!node) return;
    const distance = Math.max(180, node.clientWidth * 0.55);
    node.scrollBy({ left: direction === 'left' ? -distance : distance, behavior: 'smooth' });
  };

  return (
    <div className={`tabs-strip tabs-strip-${variant} ${className ?? ''}`}>
      {canScrollLeft && (
        <button
          type="button"
          className="tabs-scroll-arrow tabs-scroll-arrow-left"
          onClick={() => scrollTabs('left')}
          aria-label="Desplazar tabs a la izquierda"
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            chevron_left
          </span>
        </button>
      )}

      <div className="tabs-scroll" ref={scrollRef}>
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`tabs-item ${activeTab === tab ? 'tabs-item-active' : ''}`}
            onClick={() => onTabChange(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {canScrollRight && (
        <button
          type="button"
          className="tabs-scroll-arrow tabs-scroll-arrow-right"
          onClick={() => scrollTabs('right')}
          aria-label="Desplazar tabs a la derecha"
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            chevron_right
          </span>
        </button>
      )}
    </div>
  );
}
